/**
 * Web Bluetooth driver for BOOKOO scales (Themis Ultra; the Mini speaks the same protocol).
 * Wire format lives in bookooProtocol.ts; see docs/bookoo-protocol.md.
 *
 * Lifecycle: the registry (registry.ts) creates a BookooScale for a device the shared picker or Chrome's getDevices()
 * returned. connect() opens GATT and checks for the Bookoo service; later calls (the store's auto-reconnect,
 * "Reconnect") reuse the same BluetoothDevice and only re-open GATT.
 *
 * There is no handshake: enabling notifications on FF11 starts the ~10 Hz weight stream. Commands are
 * serialized through a queue (Web Bluetooth rejects overlapping GATT operations) and spaced at least
 * 100 ms apart, starting 100 ms after subscribing.
 *
 * Safety nets:
 * - Liveness watchdog: when no frame arrives for max(1.5 s, 10x the usual interval) the driver reports
 *   onStale(true), so the last weight is not shown as live. A much longer silence while the page is visible
 *   tears the link down so the store's normal reconnect path runs.
 * - Checksum diagnostics: a run of weight frames that all fail their checksum raises onWarning instead of
 *   silently showing nothing. If the checksum byte of every failing frame is 00 (as the first Ultra doc
 *   showed), such frames are accepted from then on.
 * - Keep-awake (0x25) is only sent while it helps; see KeepAwakeMode.
 *
 * Optional capabilities (types.ts): keepAwake, diagnostics (packet log summary, checksum escape hatch), tareCheck (the
 * scale ignores tare in some states without saying so), beep (every weight frame reports the level in byte 16) and, on
 * the Ultra, powerOff.
 *
 * Power-off: only the Ultra documents a shutdown command (0x15, fw >= 4.0.0), so `powerOff` exists only when the
 * device name says Ultra. Nothing confirms it; the link simply drops when the scale switches off.
 */
import { connectTimeout, describeBleError, getDriverService, pickWriteMode, ScaleServiceMissingError } from './bluetooth';
import {
  BOOKOO_COMMAND_CHAR_UUID,
  BOOKOO_SERVICE_UUID,
  BOOKOO_WEIGHT_CHAR_UUID,
  BOOKOO_NAME_PREFIXES,
  BookooPacketType,
  bookooCommands,
  bookooModelFromName,
  isBookooScaleName,
  isDocumentedFrameType,
  parsePacketDetailed,
  splitFrames,
  type BookooAutoEvent,
  type BookooChecksumMode,
  type BookooModel,
  type BookooPacket,
  type BookooPowderPacket,
  type BookooWeightPacket,
} from './bookooProtocol';
import type {
  BeepCapability,
  BluetoothScaleDriverDescriptor,
  KeepAwakeCapability,
  KeepAwakeMode,
  RawPacketListener,
  RawWriteListener,
  ScaleConnectOptions,
  ScaleDiagnosticsCapability,
  ScaleDiagnosticsItem,
  ScaleDriver,
  ScaleReading,
  ScaleWarning,
  TareCheckCapability,
} from './types';

const CONNECT_TIMEOUT_MS = 15_000;
const CONNECT_RETRY_DELAY_MS = 600;
const WRITE_RETRY_DELAY_MS = 150;
/** Minimum spacing between command writes, and between subscribing and the first write (aiobookoo's spacing). */
export const MIN_WRITE_GAP_MS = 100;

/** While keep-awake is wanted, ping at most this often: far below the scale's 5-minute minimum auto-off. */
export const KEEP_AWAKE_INTERVAL_MS = 60_000;
/** How often the keep-awake timer re-checks whether a ping is due (no BLE traffic unless it is). */
const KEEP_AWAKE_CHECK_MS = 15_000;
/** A weight change larger than this counts as someone using the scale. */
const KEEP_AWAKE_ACTIVITY_G = 1;
/** Activity keeps the pings going for max(this, half the scale's auto-off time); then the scale may power off. */
const KEEP_AWAKE_MIN_WINDOW_MS = 2 * 60_000;
/** Assumed auto-off time until a weight frame reports the real one (the scale's minimum). */
const DEFAULT_STANDBY_MIN = 5;

const WATCHDOG_CHECK_MS = 500;
/** No frame for max(STALE_MIN_MS, STALE_INTERVAL_FACTOR x median interval) = stale. */
export const STALE_MIN_MS = 1500;
const STALE_INTERVAL_FACTOR = 10;
/** Silence, counted only while the page is visible, after which the link is dropped to force a reconnect. */
export const STALE_DISCONNECT_MS = 12_000;
const INTERVAL_SAMPLES = 16;

/** Consecutive weight frames that fail their checksum before the driver reacts (~2 s at 10 Hz). */
export const CHECKSUM_FAILURE_THRESHOLD = 20;

/** Shown when a tare doesn't take: the Ultra silently ignores it in some states. */
export const BOOKOO_TARE_HINT =
  'The scale ignores tare while charging and while its auto mode is brewing or showing a result. Use weight mode (or a manual timing/ratio mode) for the guided workflow.';

export const BOOKOO_CHECKSUM_WARNING =
  'Scale connected, but every packet fails its checksum (firmware mismatch), so no weight can be shown.';

/**
 * Registry entry (registry.ts). The picker filters by name prefix and by the 0x0FFE service. Bluefy gets the first
 * prefix only: it has been reported to reject multiple filters, and Mini and Ultra both advertise "BOOKOO_SC…" there.
 *
 * Keep-awake (KeepAwakeMode): the 0x25 command exists from Ultra fw 4.0.1, so 'auto' sends it to an Ultra only.
 */
export const bookooDriver: BluetoothScaleDriverDescriptor = {
  kind: 'bookoo',
  transport: 'bluetooth',
  label: 'BOOKOO Themis Ultra / Mini',
  shortName: 'Bookoo',
  filters: [...BOOKOO_NAME_PREFIXES.map((namePrefix) => ({ namePrefix })), { services: [BOOKOO_SERVICE_UUID] }],
  bluefyFilter: { namePrefix: BOOKOO_NAME_PREFIXES[0] },
  optionalServices: [BOOKOO_SERVICE_UUID],
  primaryServices: [BOOKOO_SERVICE_UUID],
  matches: isBookooScaleName,
  create: (device) => new BookooScale(device),
  capabilities: ['keepAwake', 'diagnostics', 'tareCheck', 'beep', 'powerOff'],
  copy: {
    pickerName: 'BOOKOO_SC…',
    vendorApp: 'the BOOKOO app',
    controlsNote:
      'Tare does nothing while the Bookoo is charging or while its auto mode is brewing or showing a result: use weight mode. The timer buttons only work in the scale’s timing/ratio modes.',
    capabilityNotes: {
      keepAwake: 'Auto: Themis Ultra only.',
      powerOff: 'Bookoo: Themis Ultra only (firmware 4.0.0 or later), and not while it’s charging.',
    },
  },
};

/**
 * Scale-side auto-mode state, tracked from 0x0D event frames (Ultra fw >= 4 / beta >= 3.2.4).
 * Diagnostics only: tare is always sent, because a missed exit event must never lock tare out.
 */
export type BookooAutoModeState = 'unknown' | 'idle' | 'ready' | 'running' | 'done';

export interface BookooDiagnostics {
  model: BookooModel;
  /** Frames received (after splitting multi-frame notifications). */
  packets: number;
  weightPackets: number;
  /** Weight / event / powder frames dropped because their checksum didn't match. */
  badChecksum: number;
  /** Frames of other (undocumented) types that failed the checksum. */
  badChecksumOther: number;
  /** Wrong length or non-scale header. */
  malformed: number;
  unknown: number;
  lastPacketAt: number | null;
  lastWeight: BookooWeightPacket | null;
  lastPowder: BookooPowderPacket | null;
  lastEvent: BookooAutoEvent | null;
  autoMode: BookooAutoModeState;
  writeMode: 'withResponse' | 'withoutResponse' | 'legacy' | null;
  checksumMode: BookooChecksumMode;
  /** No data for longer than the watchdog allows. */
  stale: boolean;
  medianIntervalMs: number | null;
  keepAwakeSent: number;
}

interface Session {
  server: BluetoothRemoteGATTServer;
  commandChar: BluetoothRemoteGATTCharacteristic;
  timers: Array<ReturnType<typeof setInterval>>;
  /** Undo functions for listeners added during this session. */
  cleanup: Array<() => void>;
  // Liveness
  lastFrameAt: number;
  /** Last time the page became visible again or the watchdog timer itself was frozen. */
  resumeAt: number;
  lastCheckAt: number;
  framesSeen: number;
  intervals: number[];
  stale: boolean;
  // Checksum diagnostics
  badWeightStreak: number;
  badWeightNonZero: boolean;
  warning: ScaleWarning | null;
  // Keep-awake
  lastActivityAt: number;
  activityRefG: number | null;
  lastKeepAwakeAt: number;
  /** Shutdown was sent: no more keep-awake pings for this session. */
  shutdownSent: boolean;
}

export class BookooScale implements ScaleDriver {
  readonly kind = 'bookoo' as const;

  private readonly device: BluetoothDevice;
  private session: Session | null = null;
  private connecting: Promise<void> | null = null;
  /** Incremented to invalidate an in-flight connection attempt (timeout / disconnect()). */
  private attempt = 0;
  private writeChain: Promise<void> = Promise.resolve();
  /** When the previous write settled (or the session opened): the next one waits MIN_WRITE_GAP_MS after it. */
  private lastWriteAt = Number.NEGATIVE_INFINITY;
  private checksumMode: BookooChecksumMode = 'strict';
  private keepAwakeMode: KeepAwakeMode = 'auto';
  private awakeHold = false;
  private readonly readingListeners = new Set<(r: ScaleReading) => void>();
  private readonly disconnectListeners = new Set<(reason?: string) => void>();
  private readonly rawListeners = new Set<RawPacketListener>();
  private readonly writeListeners = new Set<RawWriteListener>();
  private readonly packetListeners = new Set<(p: BookooPacket) => void>();
  private readonly staleListeners = new Set<(stale: boolean) => void>();
  private readonly warningListeners = new Set<(w: ScaleWarning | null) => void>();
  private diag: BookooDiagnostics = emptyDiagnostics('unknown');

  /** Optional capabilities (types.ts). */
  readonly keepAwake: KeepAwakeCapability = {
    setMode: (mode) => this.setKeepAwakeMode(mode),
    setHold: (held) => this.setAwakeHold(held),
  };

  readonly diagnostics: ScaleDiagnosticsCapability = {
    summary: () => ({ badge: this.diag.model === 'unknown' ? 'Bookoo' : `Bookoo ${this.diag.model}`, items: diagnosticsItems(this.stats) }),
    acceptUnverifiedPackets: () => this.setChecksumMode('ignore'),
  };

  readonly tareCheck: TareCheckCapability = {
    failureMessage: () => {
      const autoMode = this.diag.autoMode;
      const busyAuto = autoMode === 'running' || autoMode === 'done' ? ' The scale reports its auto mode is brewing or showing a result.' : '';
      return `The scale didn’t zero.${busyAuto} ${BOOKOO_TARE_HINT}`;
    },
  };

  /**
   * Off, low, medium, high: the Ultra's range (fw >= 4). The Mini's doc allows 0-5, so a Mini may report a level above
   * 3 that was set in the BOOKOO app.
   */
  readonly beep: BeepCapability = {
    levels: 3,
    // async: an out-of-range level rejects rather than throws.
    setLevel: async (level) => this.setBeep(level),
  };

  constructor(device: BluetoothDevice) {
    this.device = device;
    this.diag.model = bookooModelFromName(device.name);
  }

  get name(): string {
    return this.device.name?.trim() || 'Bookoo scale';
  }

  get connected(): boolean {
    return this.session?.server.connected === true;
  }

  /** From the advertised name: the BluetoothDevice is only known once the picker returned one. */
  get model(): BookooModel {
    return bookooModelFromName(this.device.name);
  }

  /**
   * Switch the scale off (0x15). Only the Themis Ultra documents this (fw >= 4.0.0; ignored while charging), so it
   * is undefined for every other model and they keep their own auto-off. Older Ultra firmware probably ignores it.
   */
  get powerOff(): (() => Promise<void>) | undefined {
    return this.model === 'ultra' ? this.sendShutdown : undefined;
  }

  private readonly sendShutdown = (): Promise<void> => {
    // Not activity: it must not trigger a keep-awake ping right behind it.
    const result = this.send('switch off', bookooCommands.shutdown(), false);
    if (this.session) this.session.shutdownSent = true;
    return result;
  };

  /** Frames dropped because their checksum didn't match (debug view). */
  get badChecksumCount(): number {
    return this.diag.badChecksum;
  }

  /** Everything the driver tracks about the link and the frames (the packet log shows a summary). */
  get stats(): BookooDiagnostics {
    return { ...this.diag, medianIntervalMs: this.session ? median(this.session.intervals) : null };
  }

  connect(options: ScaleConnectOptions = {}): Promise<void> {
    if (this.connected) return Promise.resolve();
    if (this.connecting) return this.connecting;
    // The link died without a gattserverdisconnected event (yet): drop the stale session quietly.
    this.endSession();
    const attempt = ++this.attempt;
    const p = this.establish(attempt, options.timeoutMs ?? CONNECT_TIMEOUT_MS).finally(() => {
      if (this.connecting === p) this.connecting = null;
    });
    this.connecting = p;
    return p;
  }

  /** Closes the session, or cancels (and aborts) a pending connection attempt. Safe to call repeatedly. */
  async disconnect(): Promise<void> {
    this.attempt++; // abandon any in-flight connection attempt
    const hadSession = this.session !== null;
    const active = hadSession || this.connecting !== null;
    this.connecting = null;
    this.endSession();
    const device = this.device;
    device.removeEventListener('gattserverdisconnected', this.onGattDisconnected);
    // Also aborts a still-pending gatt.connect(). Only when this instance owns the link: another instance
    // may since have connected to the same BluetoothDevice.
    if (active) device.gatt?.disconnect();
    if (hadSession) this.emitDisconnect();
  }

  /**
   * Always sent. The scale ignores it while charging and in auto mode running/done, and never acknowledges
   * it, so callers confirm a tare by watching the weight settle near zero (see BOOKOO_TARE_HINT).
   */
  tare(): Promise<void> {
    return this.send('tare', bookooCommands.tare());
  }

  /** Timer commands only take effect in the scale's timing/ratio modes (ignored in weight mode). */
  startTimer(): Promise<void> {
    return this.send('start timer', bookooCommands.startTimer());
  }

  stopTimer(): Promise<void> {
    return this.send('stop timer', bookooCommands.stopTimer());
  }

  resetTimer(): Promise<void> {
    return this.send('reset timer', bookooCommands.resetTimer());
  }

  /** Tare + reset + start timer in one command (timing/ratio mode; starts extraction in auto-ready). */
  tareAndStartTimer(): Promise<void> {
    return this.send('tare and start timer', bookooCommands.tareAndStartTimer());
  }

  setFlowSmoothing(on: boolean): Promise<void> {
    return this.send('flow smoothing', bookooCommands.setFlowSmoothing(on));
  }

  setBeep(level: number): Promise<void> {
    return this.send('beep volume', bookooCommands.setBeep(level));
  }

  setAutoOff(minutes: number): Promise<void> {
    return this.send('auto-off', bookooCommands.setAutoOff(minutes));
  }

  setKeepAwakeMode(mode: KeepAwakeMode): void {
    this.keepAwakeMode = mode;
    if (this.session) this.maybeKeepAwake(this.session);
  }

  /** The app is actively using the scale (guided brew, scale page): keep it awake while the page is visible. */
  setAwakeHold(held: boolean): void {
    this.awakeHold = held;
    if (held && this.session) this.maybeKeepAwake(this.session);
  }

  /** Change how incoming checksums are validated, e.g. 'ignore' after the user accepts unverified packets. */
  setChecksumMode(mode: BookooChecksumMode): void {
    this.checksumMode = mode;
    this.diag.checksumMode = mode;
  }

  onReading(cb: (r: ScaleReading) => void): () => void {
    return subscribe(this.readingListeners, cb);
  }

  onDisconnect(cb: (reason?: string) => void): () => void {
    return subscribe(this.disconnectListeners, cb);
  }

  /** Every 20-byte frame as received, before validation. */
  onRawPacket(cb: RawPacketListener): () => void {
    return subscribe(this.rawListeners, cb);
  }

  /** Every write attempt to the scale, with whether it went out. */
  onRawWrite(cb: RawWriteListener): () => void {
    return subscribe(this.writeListeners, cb);
  }

  /** Every successfully decoded frame (weight, powder, auto-mode events, unknown types). */
  onPacket(cb: (p: BookooPacket) => void): () => void {
    return subscribe(this.packetListeners, cb);
  }

  onStale(cb: (stale: boolean) => void): () => void {
    return subscribe(this.staleListeners, cb);
  }

  onWarning(cb: (warning: ScaleWarning | null) => void): () => void {
    return subscribe(this.warningListeners, cb);
  }

  // ---------------------------------------------------------------- connection

  private async establish(attempt: number, timeoutMs: number): Promise<void> {
    const device = this.device;
    // addEventListener ignores duplicates of the same listener, so this is safe on every reconnect.
    device.addEventListener('gattserverdisconnected', this.onGattDisconnected);

    const timeout = connectTimeout(timeoutMs);
    try {
      await Promise.race([this.openSessionWithRetry(device, attempt), timeout.promise]);
    } catch (e) {
      // A superseded attempt was already cleaned up by whoever superseded it (disconnect() or a newer
      // attempt); touching the session or GATT now could tear down that newer connection.
      if (attempt === this.attempt) {
        this.attempt++;
        this.endSession();
        // Also aborts a still-pending gatt.connect().
        device.gatt?.disconnect();
      }
      throw describeBleError(e, bookooDriver);
    } finally {
      timeout.clear();
    }
  }

  private async openSessionWithRetry(device: BluetoothDevice, attempt: number): Promise<void> {
    try {
      await this.openSession(device, attempt);
    } catch (e) {
      // Android's BLE stack regularly fails the first GATT connect "for unknown reason"; one retry fixes most.
      const name = e instanceof Error ? e.name : '';
      if (attempt !== this.attempt || e instanceof ScaleServiceMissingError || name === 'NotFoundError' || name === 'SecurityError') throw e;
      await delay(CONNECT_RETRY_DELAY_MS);
      this.assertCurrent(attempt);
      await this.openSession(device, attempt);
    }
  }

  private async openSession(device: BluetoothDevice, attempt: number): Promise<void> {
    const gatt = device.gatt;
    if (!gatt) throw new Error('This Bluetooth device has no GATT server.');
    const server = gatt.connected ? gatt : await gatt.connect();
    this.assertCurrent(attempt);
    // The service check: a device the registry matched by name must really be a Bookoo.
    const service = await getDriverService(server, bookooDriver);
    this.assertCurrent(attempt);
    const weightChar = await service.getCharacteristic(BOOKOO_WEIGHT_CHAR_UUID);
    const commandChar = await service.getCharacteristic(BOOKOO_COMMAND_CHAR_UUID);
    this.assertCurrent(attempt);

    const cleanup: Array<() => void> = [];
    // A listener per attempt: a superseded attempt's cleanup must not remove a newer session's listener
    // from the same characteristic object.
    const listen = (ch: BluetoothRemoteGATTCharacteristic) => {
      const onValue = (ev: Event) => this.onNotification(ev, ch);
      ch.addEventListener('characteristicvaluechanged', onValue);
      const off = () => ch.removeEventListener('characteristicvaluechanged', onValue);
      cleanup.push(off);
      return off;
    };
    try {
      listen(weightChar);
      await weightChar.startNotifications();
      this.assertCurrent(attempt);
      // Always try FF12 as well, whatever its properties claim (notify or indicate): some Ultra firmware
      // reports auto-mode events (0x0D/0x0F) or query replies there. Not fatal if it can't notify.
      const offCommand = listen(commandChar);
      try {
        await commandChar.startNotifications();
      } catch {
        offCommand();
      }
      this.assertCurrent(attempt);
    } catch (e) {
      cleanup.forEach((fn) => fn());
      throw e;
    }

    const now = performance.now();
    this.diag = emptyDiagnostics(bookooModelFromName(device.name));
    this.diag.writeMode = pickWriteMode(commandChar);
    this.diag.checksumMode = this.checksumMode;
    // The first command (keep-awake or a tare) waits MIN_WRITE_GAP_MS after subscribing.
    this.lastWriteAt = now;
    const session: Session = {
      server,
      commandChar,
      timers: [],
      cleanup,
      lastFrameAt: now,
      resumeAt: now,
      lastCheckAt: now,
      framesSeen: 0,
      intervals: [],
      stale: false,
      badWeightStreak: 0,
      badWeightNonZero: false,
      warning: null,
      // Connecting counts as using the scale.
      lastActivityAt: now,
      activityRefG: null,
      lastKeepAwakeAt: Number.NEGATIVE_INFINITY,
      shutdownSent: false,
    };
    this.session = session;
    this.startWatchdog(session);
    session.timers.push(setInterval(() => this.maybeKeepAwake(session), KEEP_AWAKE_CHECK_MS));
    this.maybeKeepAwake(session, now);
  }

  private assertCurrent(attempt: number): void {
    if (attempt !== this.attempt) throw new Error('Connection attempt was cancelled.');
  }

  private endSession(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    s.timers.forEach((t) => clearInterval(t));
    s.cleanup.forEach((fn) => fn());
  }

  /** Only reported for established sessions: failures while connecting reject connect() instead. */
  private readonly onGattDisconnected = (): void => {
    if (!this.session) return;
    this.endSession();
    this.emitDisconnect('Lost connection to the scale.');
  };

  private emitDisconnect(reason?: string): void {
    for (const cb of [...this.disconnectListeners]) safeCall(() => cb(reason));
  }

  // ---------------------------------------------------------------- liveness watchdog

  private startWatchdog(s: Session): void {
    s.timers.push(setInterval(() => this.checkLiveness(s), WATCHDOG_CHECK_MS));
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    // A page that was hidden (and possibly frozen) restarts the silence clock instead of blaming the scale.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') return;
      const now = performance.now();
      s.resumeAt = now;
      s.lastCheckAt = now;
    };
    document.addEventListener('visibilitychange', onVisibility);
    s.cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  private checkLiveness(s: Session): void {
    if (this.session !== s) return;
    const now = performance.now();
    const sinceLastCheck = now - s.lastCheckAt;
    s.lastCheckAt = now;
    if (!pageVisible()) return;
    if (sinceLastCheck > WATCHDOG_CHECK_MS * 4) {
      // The timer itself was frozen or throttled (background tab, device sleep): our clock is unreliable.
      s.resumeAt = now;
      return;
    }
    const silentMs = now - Math.max(s.lastFrameAt, s.resumeAt);
    const staleAfter = staleAfterMs(s.intervals);
    if (silentMs >= Math.max(STALE_DISCONNECT_MS, staleAfter * 2)) {
      this.forceDisconnect('The scale stopped sending data.');
      return;
    }
    if (!s.stale && silentMs >= staleAfter) {
      s.stale = true;
      this.diag.stale = true;
      this.emitStale(true);
    }
  }

  private noteAlive(s: Session, t: number): void {
    if (s.framesSeen > 0) {
      s.intervals.push(t - s.lastFrameAt);
      if (s.intervals.length > INTERVAL_SAMPLES) s.intervals.shift();
    }
    s.framesSeen++;
    s.lastFrameAt = t;
    if (s.stale) {
      s.stale = false;
      this.diag.stale = false;
      this.emitStale(false);
    }
  }

  /** Drop a link that delivers nothing, so the store's reconnect path takes over. */
  private forceDisconnect(reason: string): void {
    if (!this.session) return;
    this.endSession();
    // gattserverdisconnected follows, but finds no session and stays quiet.
    this.device.gatt?.disconnect();
    this.emitDisconnect(reason);
  }

  private emitStale(stale: boolean): void {
    for (const cb of [...this.staleListeners]) safeCall(() => cb(stale));
  }

  // ---------------------------------------------------------------- keep-awake

  private keepAwakeWanted(s: Session, now: number): boolean {
    if (this.keepAwakeMode === 'never' || s.shutdownSent) return false;
    if (this.keepAwakeMode === 'auto' && this.diag.model !== 'ultra') return false;
    if (this.awakeHold && pageVisible()) return true;
    const standbyMin = this.diag.lastWeight?.standbyMin || DEFAULT_STANDBY_MIN;
    return now - s.lastActivityAt < Math.max(KEEP_AWAKE_MIN_WINDOW_MS, (standbyMin * 60_000) / 2);
  }

  private maybeKeepAwake(s: Session, now = performance.now()): void {
    if (this.session !== s || now - s.lastKeepAwakeAt < KEEP_AWAKE_INTERVAL_MS || !this.keepAwakeWanted(s, now)) return;
    s.lastKeepAwakeAt = now;
    this.diag.keepAwakeSent++;
    // Failures are irrelevant: the next due ping tries again.
    void this.send('keep awake', bookooCommands.keepAwake(), false).catch(() => undefined);
  }

  private markActivity(s: Session, now: number): void {
    s.lastActivityAt = now;
    this.maybeKeepAwake(s, now);
  }

  // ---------------------------------------------------------------- incoming data

  private onNotification(ev: Event, ch: BluetoothRemoteGATTCharacteristic): void {
    // Bluefy dispatches plain objects rather than real Events; fall back to the characteristic itself.
    const value = (ev?.target as BluetoothRemoteGATTCharacteristic | null | undefined)?.value ?? ch.value;
    if (!value) return;
    const t = performance.now();
    // Any notification, even one that fails validation, proves the link is alive.
    if (this.session) this.noteAlive(this.session, t);
    // Copy: Chrome may reuse the underlying buffer, and raw listeners may keep the bytes.
    const bytes = new Uint8Array(value.byteLength);
    bytes.set(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    for (const frame of splitFrames(bytes)) this.handleFrame(frame, t);
  }

  private handleFrame(frame: Uint8Array, t: number): void {
    const d = this.diag;
    d.packets++;
    d.lastPacketAt = t;
    for (const cb of [...this.rawListeners]) safeCall(() => cb(frame, t));

    const result = parsePacketDetailed(frame, { checksum: this.checksumMode });
    if (!result.ok) {
      if (result.reason === 'checksum') this.checksumFailed(frame);
      else d.malformed++;
      return;
    }
    const packet = result.packet;
    for (const cb of [...this.packetListeners]) safeCall(() => cb(packet));

    switch (packet.type) {
      case 'weight': {
        d.weightPackets++;
        d.lastWeight = packet;
        if (this.session) this.weightFrameAccepted(this.session, packet, t);
        const reading: ScaleReading = {
          t,
          weightG: packet.weightG,
          flowGs: packet.flowGs,
          timerMs: packet.timerMs,
          batteryPct: packet.batteryPct,
          beepLevel: packet.buzzer,
        };
        for (const cb of [...this.readingListeners]) safeCall(() => cb(reading));
        break;
      }
      case 'powder':
        d.lastPowder = packet;
        break;
      case 'event':
        d.lastEvent = packet.event;
        d.autoMode = nextAutoMode(d.autoMode, packet.event);
        break;
      case 'unknown':
        d.unknown++;
        break;
    }
  }

  private checksumFailed(frame: Uint8Array): void {
    const d = this.diag;
    if (!isDocumentedFrameType(frame[1])) {
      d.badChecksumOther++;
      return;
    }
    d.badChecksum++;
    const s = this.session;
    if (!s || frame[1] !== BookooPacketType.weight) return;
    s.badWeightStreak++;
    if (frame[frame.length - 1] !== 0) s.badWeightNonZero = true;
    if (s.badWeightStreak < CHECKSUM_FAILURE_THRESHOLD) return;
    if (this.checksumMode === 'strict' && !s.badWeightNonZero && d.weightPackets === 0) {
      // Every failing frame carries 00 where the checksum belongs, as BOOKOO's first Ultra doc showed.
      // Accept those from now on (BLE's link-layer CRC already protects the payload).
      console.info('Bookoo scale sends 00 instead of an XOR checksum; accepting such frames.');
      this.setChecksumMode('allowZero');
      s.badWeightStreak = 0;
      return;
    }
    if (!s.warning) this.setWarning(s, { code: 'checksum', message: BOOKOO_CHECKSUM_WARNING });
  }

  private weightFrameAccepted(s: Session, packet: BookooWeightPacket, t: number): void {
    s.badWeightStreak = 0;
    s.badWeightNonZero = false;
    if (s.warning) this.setWarning(s, null);
    if (s.activityRefG === null) {
      s.activityRefG = packet.weightG;
    } else if (Math.abs(packet.weightG - s.activityRefG) > KEEP_AWAKE_ACTIVITY_G) {
      s.activityRefG = packet.weightG;
      this.markActivity(s, t);
    }
  }

  private setWarning(s: Session, warning: ScaleWarning | null): void {
    s.warning = warning;
    for (const cb of [...this.warningListeners]) safeCall(() => cb(warning));
  }

  // ---------------------------------------------------------------- commands

  /**
   * Queue a command write behind any in-flight write, at least MIN_WRITE_GAP_MS after the previous one;
   * retries once on failure. Every command except keep-awake itself counts as using the scale.
   */
  private send(label: string, frame: Uint8Array<ArrayBuffer>, activity = true): Promise<void> {
    const run = () => this.writeSpaced(label, frame);
    const result = this.writeChain.then(run, run);
    this.writeChain = result.catch(() => undefined);
    // After queueing, so a keep-awake triggered by this never delays the command itself.
    if (activity && this.session) this.markActivity(this.session, performance.now());
    return result;
  }

  private async writeSpaced(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const wait = this.lastWriteAt + MIN_WRITE_GAP_MS - performance.now();
    if (wait > 0) await delay(wait);
    if (!this.connected) throw commandError(label, new Error('Scale not connected'));
    try {
      await this.writeWithRetry(label, frame);
    } finally {
      this.lastWriteAt = performance.now();
    }
  }

  private async writeWithRetry(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    try {
      await this.writeAttempt(label, frame);
    } catch (first) {
      if (!this.connected) throw commandError(label, first);
      await delay(WRITE_RETRY_DELAY_MS);
      try {
        await this.writeAttempt(label, frame);
      } catch (second) {
        throw commandError(label, second);
      }
    }
  }

  /** One write, reported to the onRawWrite listeners whether it went out or not. */
  private async writeAttempt(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    let ok = false;
    try {
      await this.writeOnce(frame);
      ok = true;
    } finally {
      const t = performance.now();
      for (const cb of [...this.writeListeners]) safeCall(() => cb(frame, label, t, ok));
    }
  }

  private async writeOnce(frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const s = this.session;
    if (!s || !s.server.connected) throw new Error('Scale not connected');
    const ch = s.commandChar;
    // A fresh buffer the size of the frame: Bluefy has been reported to send a view's whole underlying buffer.
    const bytes = frame.slice();
    switch (pickWriteMode(ch)) {
      case 'withResponse':
        return ch.writeValueWithResponse(bytes);
      case 'withoutResponse':
        return ch.writeValueWithoutResponse(bytes);
      case 'legacy':
        return ch.writeValue(bytes);
    }
  }
}

// ---------------------------------------------------------------- helpers

function nextAutoMode(current: BookooAutoModeState, event: BookooAutoEvent): BookooAutoModeState {
  switch (event) {
    case 'ready':
      return 'ready';
    case 'started':
      return 'running';
    case 'stopped':
      return 'done';
    case 'exitReady':
    case 'exitDone':
      return 'idle';
    case 'unknown':
      return current;
  }
}

/** The packet log's diagnostics line. */
function diagnosticsItems(d: BookooDiagnostics): ScaleDiagnosticsItem[] {
  const items: ScaleDiagnosticsItem[] = [
    { text: `${d.packets} frames` },
    { text: `${d.weightPackets} weight` },
    { text: `${d.badChecksum} bad checksum`, tone: d.badChecksum > 0 ? 'warning' : undefined },
  ];
  if (d.badChecksumOther > 0) items.push({ text: `${d.badChecksumOther} bad checksum (other types)` });
  items.push({ text: `${d.malformed} malformed`, tone: d.malformed > 0 ? 'warning' : undefined }, { text: `${d.unknown} unknown` }, { text: `auto mode: ${d.autoMode}` });
  if (d.writeMode) items.push({ text: `writes: ${d.writeMode}` });
  items.push({ text: `checksum: ${d.checksumMode}`, tone: d.checksumMode !== 'strict' ? 'warning' : undefined });
  if (d.medianIntervalMs != null) items.push({ text: `~${Math.round(d.medianIntervalMs)} ms/frame` });
  if (d.stale) items.push({ text: 'no data (stale)', tone: 'danger' });
  items.push({ text: `${d.keepAwakeSent} keep-awake` });
  return items;
}

function emptyDiagnostics(model: BookooModel): BookooDiagnostics {
  return {
    model,
    packets: 0,
    weightPackets: 0,
    badChecksum: 0,
    badChecksumOther: 0,
    malformed: 0,
    unknown: 0,
    lastPacketAt: null,
    lastWeight: null,
    lastPowder: null,
    lastEvent: null,
    autoMode: 'unknown',
    writeMode: null,
    checksumMode: 'strict',
    stale: false,
    medianIntervalMs: null,
    keepAwakeSent: 0,
  };
}

function median(xs: readonly number[]): number | null {
  if (xs.length === 0) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** Based on the measured frame rate, not an assumed 10 Hz. */
function staleAfterMs(intervals: readonly number[]): number {
  const m = median(intervals);
  return Math.max(STALE_MIN_MS, m === null ? 0 : m * STALE_INTERVAL_FACTOR);
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function commandError(label: string, cause: unknown): Error {
  const msg = cause instanceof Error ? cause.message : String(cause);
  return new Error(`Couldn't send "${label}" to the scale: ${msg}`, { cause });
}

function subscribe<T>(set: Set<T>, cb: T): () => void {
  set.add(cb);
  return () => {
    set.delete(cb);
  };
}

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    console.error('Bookoo scale listener failed', e);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
